import Vue from 'vue';
import store from '../store';
import { formatData } from '@/constants/commonContent';
import { messages } from '@/constants/messages';
import * as Web3Utils from 'web3-utils';
import validate from 'bitcoin-address-validation';
import { EMAIL_SELECT } from './commonContent';

export const defaultFormData = {
  title: '',
  descr1: '',
  descr2: '',
  form: {
    elements: [],
    submit (data) {},
    reset () {}
  }
};

export const validData = {
  ethMinValuePattern: /^\s*(?=.*[1-9])\d*(?:\.\d{1,18})?\s*$/gm,
  companyUpdate: {
    min: 1,
    max: 500
  },
  companyInfo: {
    name: {
      min: 1,
      max: 80
    },
    about: {
      min: 0,
      max: 1000
    }
  },
  member: {
    name: {
      min: 2,
      max: 80
    },
    email: {
      min: 6,
      max: 80
    },
    position: {
      min: 2,
      max: 80
    }
  },
  token: {
    name: {
      min: 1,
      max: 80
    },
    symbol: {
      min: 1,
      max: 80
    },
    amount: {
      min: 1,
      max: 4200000000
    }
  },
  newFeature: {
    request: {
      min: 1,
      max: 500
    }
  },
  voting: {
    title: {
      min: 1,
      max: 80
    },
    about: {
      min: 1,
      max: 500
    },
    percent: {
      min: 0,
      max: 100
    }
  },
  finStatFile: {
    title: {
      min: 1,
      max: 80
    }
  },
  financial: {
    currency: {
      min: 1,
      max: 80
    },
    address: {
      min: 1,
      max: 80
    },
    ETH: {
      validateAddr: address => Web3Utils.isAddress(address)
    },
    BTC: {
      validateAddr: address => validate(address)
    }
  },
  order: {
    price: {
      min: 0
    }
  },
  legalFile: {
    title: {
      min: 1,
      max: 80
    }
  },
  gettingContact: {
    message: {
      min: 5
    }
  },
  communication: {
    message: {
      min: 5
    }
  },
  tradeNow: {
    amountOfShares: {
      pattern: /^(?!0)(\d)+$/
    }
  }
};

function getBase64 (file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });
}

const formatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0
});

export function requestEditInfo (data): any {
  return {
    title: 'EDIT COMPANY INFORMATION',
    descr1: 'Please complete the information below in order to update the company information.',
    descr2: 'Once ready click on ‘UPDATE’ and confirm the transaction via Metamask wallet.',
    form: {
      elements: [
        {
          type: 'input',
          data: {
            type: 'text',
            name: 'company-input',
            label: 'Company name',
            model: data.name,
            min: validData.companyInfo.name.min,
            max: validData.companyInfo.name.max,
            required: true
          }
        },
        {
          type: 'input',
          data: {
            type: 'textarea',
            name: 'textarea-input',
            label: 'Description',
            model: data.about,
            min: validData.companyInfo.about.min,
            max: validData.companyInfo.about.max,
            required: true
          }
        },
        {
          type: 'button',
          data: {
            type: 'submit',
            value: 'UPDATE',
            disabled: false
          }
        }
      ],
      submit (data) {
        store.commit('companyInfoPending', true);
        const requestData = { name: data[0], about: data[1] };
        store.dispatch('addCompanyInfoDapp', requestData);
      }
    }
  };
}

export function requestApproveNewMember (data, id): any {
  return {
    title: 'APPROVE NEW MEMBER',
    descr1:
      'Please review the information provided below and ensure that the user is legit to join your company.',
    descr2:
      'Once you have click on ‘APPROVE’ confirm the transaction via Metamask. Only at that point your upload will be available.',
    form: {
      elements: [
        {
          type: 'input',
          data: {
            type: 'text',
            label: 'Full name',
            name: 'Full name',
            model: data.name,
            disabled: true,
            required: false
          }
        },
        {
          type: 'input',
          data: {
            type: 'email',
            label: 'Email',
            name: 'Email',
            model: data.email,
            disabled: true,
            required: false
          }
        },
        {
          type: 'input',
          data: {
            type: 'text',
            label: 'Wallet address',
            name: 'Wallet address',
            model: data.address,
            placeholder: '',
            disabled: true,
            required: false
          }
        },
        {
          type: 'button',
          data: {
            type: 'submit',
            value: 'APPROVE',
            disabled: false
          }
        }
      ],
      submit () {
        store.commit('approveMemberPending', id);
        store.dispatch('approveMembershipDapp', data).then(() => {
          store.dispatch('getWaitingsDapp');
          store.dispatch('getMembersDapp');
        });
      }
    }
  };
}

export function requestTransferShares (data): any {
  function memberArray (items): any[] {
    const arr: any[] = [];
    const selected = { value: null, text: 'Please select ...', disabled: true };
    arr.push(selected);
    for (let addr in items) {
      if (addr !== store.getters.getWallet) {
        arr.push({ value: addr, text: items[addr].name });
      }
    }
    return arr;
  }

  return {
    title: ' TRANSFER SHARES',
    descr1: 'Please complete the information below in order to set transfer your shares.',
    descr2:
      'Once you have click on ‘TRANSFER’ confirm the transaction via Metamask. Only at that point your upload will be available.',
    form: {
      elements: [
        {
          type: 'input',
          data: {
            type: 'select',
            label: 'Receiver',
            name: 'receiver-select',
            model: memberArray(data)[0].value,
            options: memberArray(data), // { value:: 1, text: 'text' }
            disabled: false,
            required: true
          }
        },
        {
          type: 'input',
          data: {
            type: 'number',
            label: 'Amount',
            name: 'amount-input',
            model: '',
            min: <number>validData.token.amount.min,
            max: store.getters.getTokenBalance() - store.getters.getFreezedOf,
            pattern: /^(?!0)(\d)+$/,
            required: true
          }
        },
        {
          type: 'text',
          data: {
            class: 'text-custom-pos',
            value:
              '// max amount available for transfer: ' +
              formatter.format(
                store.getters.getTokenBalance()
                  ? store.getters.getTokenBalance() - store.getters.getFreezedOf
                  : 0
              )
          }
        },
        {
          type: 'button',
          data: {
            type: 'submit',
            value: 'TRANSFER',
            disabled: false
          }
        }
      ],
      submit (data) {
        store.commit('transferTokenPending', true);
        const res = { to: data[0], value: parseInt(data[1]) };
        store.commit('setMailPending', 'transfersShares');
        store.dispatch('transferDapp', res);
      },
      reset () {
        console.log('reset');
      }
    }
  };
}

export function requestApproveNewFeature (data): any {
  return {
    title: 'APPROVE NEW FEATURE',
    descr1: 'Good news! A new features is now available to your company.',
    descr2:
      'Once you have click on ‘APPROVE’ confirm the transaction via Metamask. Only at that point your upload will be available.',
    form: {
      elements: [
        {
          type: 'textarea',
          data: {
            name: 'textarea-input',
            label: 'Text',
            model: '',
            placeholder:
              'Add a new function to your menu that allows to upload legal documents and to your incorporation agent to upload  all material directly to your dashboard. A new icon will appear after the update and every shareholder of the company will have access to all material.',
            min: validData.newFeature.request.min,
            max: validData.newFeature.request.max,
            required: true
          }
        },
        {
          type: 'button',
          data: {
            type: 'submit',
            value: 'APPROVE',
            disabled: false
          }
        }
      ],
      submit (data) {
        console.log('submit:', data);
      },
      reset () {
        console.log('reset');
      }
    }
  };
}

export function requestUpdateYourProfile (member, avatar): any {
  return {
    title: 'UPDATE YOUR PROFILE',
    descr1: 'Please remember that the information in your profile must be real.',
    descr2:
      'Once you have click on ‘UPDATE’ confirm the transaction via Metamask. Only at that point your upload will be available.',
    form: {
      elements: [
        {
          type: 'input',
          data: {
            type: 'text',
            label: 'Name',
            name: 'name-input',
            model: member.name,
            min: validData.member.name.min,
            max: validData.member.name.max,
            required: true
          }
        },
        {
          type: 'button',
          data: {
            type: 'button',
            label: 'Profile picture',
            accept: '.jpg, .png, .gif',
            name: 'profile-picture',
            value: avatar ? 'Change picture' : 'Browse your files…',
            required: false
          }
        },
        {
          type: 'input',
          data: {
            type: 'email',
            label: 'E-mail',
            name: 'email-input',
            model: member.email,
            min: validData.member.email.min,
            max: validData.member.email.max,
            required: true
          }
        },
        {
          type: 'input',
          data: {
            type: 'text',
            name: 'role-input',
            label: 'Role in the company ',
            model: member.position ? member.position : '',
            min: validData.member.position.min,
            max: validData.member.position.max,
            required: true
          }
        },
        {
          type: 'button',
          data: {
            type: 'submit',
            value: 'UPDATE',
            disabled: false
          }
        }
      ],
      submit (data) {
        store.commit('updateMemberPending', true);

        const def = '';

        if (data[2]) {
          store.getters.ipfs.add({ file: data[2] }).then(
            response => {
              updateMember(response);
            },
            error => {
              store.commit('updateMemberPending', false);
            }
          );
        } else {
          updateMember(def); // '' - none
        }

        function updateMember (avatar) {
          const update = {
            name: data[0] !== member.name ? data[0] : def,
            avatarName: data[1],
            avatar,
            email: data[3] !== member.email ? data[3] : def,
            position: data[4] !== member.position ? data[4] : def
          };
          if (
            update.avatar !== def ||
            update.email !== def ||
            update.position !== def ||
            update.name !== def
          ) {
            store.dispatch('updateMemberDapp', update);
          } else store.commit('updateMemberPending', false);
        }
      }
    }
  };
}

export function requestResolveVoting (data): any {
  return {
    title: 'RESOLVE VOTE',
    descr1: 'The voting is terminated and it’s time to see the result. Follow step below.',
    descr2:
      'Once you have click on ‘RESOLVE’ confirm the transaction via Metamask. Only at that point your upload will be available.',
    form: {
      elements: [
        {
          type: 'input',
          data: {
            type: 'text',
            name: 'title-input',
            label: 'Title',
            placeholder: data.subject,
            disabled: true
          }
        },
        {
          type: 'input',
          data: {
            type: 'textarea',
            name: 'desc-textarea',
            label: 'Description',
            placeholder: data.description,
            disabled: true
          }
        },
        {
          type: 'button',
          data: {
            type: 'submit',
            value: 'RESOLVE',
            disabled: false
          }
        }
      ],
      submit () {
        store.commit('newVotePending', data.id);
        store.commit('setMailPending', 'resolveVoting');
        store.dispatch('resolveVotingDapp', data);
      },
      reset () {
        console.log('reset');
      }
    }
  };
}

export function requestMembership (): any {
  return {
    title: 'JOIN THE COMPANY',
    descr1: 'Please complete the information below in order to join the company.',
    descr2:
      'Once you have click on ‘REQUEST NOW’ confirm the transaction via Metamask. Director will approve the request',
    form: {
      elements: [
        {
          type: 'input',
          data: {
            type: 'text',
            name: 'name-input',
            label: 'Name',
            model: '',
            min: validData.member.name.min,
            max: validData.member.name.max,
            required: true
          }
        },
        {
          type: 'input',
          data: {
            type: 'email',
            label: 'E-mail',
            name: 'email-input',
            model: '',
            min: validData.member.email.min,
            max: validData.member.email.max,
            required: true
          }
        },
        {
          type: 'button',
          data: {
            type: 'submit',
            value: 'REQUEST NOW',
            disabled: false
          }
        }
      ],
      submit (data) {
        store.commit('updateMemberPending', true);
        store.commit('setMailPending', 'joinToTheCompany');

        store
          .dispatch('requestMembershipDapp', {
            name: data[0],
            email: data[1],
            avatarName: '',
            avatar: ''
          })
          .then(
            response => {
              store.dispatch('isWaitingDapp');
              store.commit('updateMemberPending', false);
            },
            error => {
              store.commit('updateMemberPending', false);
            }
          );
      }
    }
  };
}

export function requestTreasuryWallet (): any {
  return {
    title: 'UPLOAD TREASURY WALLET',
    descr1: 'Please paste the address you would like to record as official treasury wallet.',
    descr2:
      'Once you have click on ‘UPDATE’ you will have to confirm the transaction via your Metamask wallet. Only at that point your upload will be available.',
    form: {
      elements: [
        {
          type: 'input',
          data: {
            type: 'text',
            name: 'address',
            label: 'Wallet address',
            model: '',
            placeholder: '0x012345...',
            size: 42,
            required: true
          }
        },
        {
          type: 'input',
          data: {
            type: 'number',
            label: 'Amount',
            name: 'amount-input',
            model: '',
            min: validData.token.amount.min,
            max: store.getters.getTokenBalance(),
            required: true
          }
        },
        {
          type: 'button',
          data: {
            type: 'submit',
            value: 'UPDATE',
            disabled: false
          }
        }
      ],
      submit (data) {
        if (store.getters.isUsedAddress(data[0])) {
          Vue.toasted.error(messages.addressNotUsed);
          return;
        }
        store.commit('companyTreasuryWalletPending', true);

        const res = { wallet: data[0], value: parseInt(data[1]) };
        store.dispatch('initTreasuryWalletDapp', res);
      }
    }
  };
}

export function requestCompanyUpdate (): any {
  return {
    title: 'NEW COMPANY UPDATE',
    descr1: 'Please provide the information below in order to publish a new company update.',
    descr2:
      'Once you have click on ‘PUBLISH’ confirm the transaction via Metamask. Only at that point your upload will be available.',
    form: {
      elements: [
        {
          type: 'input',
          data: {
            type: 'textarea',
            name: 'textarea-input',
            label: 'Description',
            model: '',
            min: validData.companyUpdate.min,
            max: validData.companyUpdate.max,
            required: true
          }
        },
        {
          type: 'button',
          data: {
            type: 'submit',
            value: 'PUBLISH',
            disabled: false
          }
        }
      ],
      submit (data) {
        store.commit('companyUpdatePending', true);
        store.dispatch('addCompanyUpdateDapp', {
          text: data[0]
        });
      },
      reset () {
        console.log('reset');
      }
    }
  };
}

export function requestNewVoting (): any {
  return {
    title: 'NEW VOTE',
    descr1: 'Please complete the information below in order to set up a new company vote.',
    descr2:
      'Once you have click on ‘LAUNCH’ confirm the transaction via Metamask. Only at that point your upload will be available.',
    form: {
      elements: [
        {
          type: 'input',
          data: {
            type: 'text',
            name: 'title-input',
            label: 'Title',
            model: '',
            min: validData.voting.title.min,
            max: validData.voting.title.max,
            required: true
          }
        },
        {
          type: 'input',
          data: {
            type: 'text',
            label: 'Description  ',
            name: 'desc-input',
            model: '',
            min: validData.voting.about.min,
            max: validData.voting.about.max,
            required: true
          }
        },
        {
          type: 'input',
          data: {
            type: 'datetime',
            label: 'Deadline',
            name: 'deadline-input',
            model: '',
            min: Vue.moment().format('YYYY-MM-DDTHH:mm:ss'),
            required: true
          }
        },
        {
          type: 'button',
          data: {
            type: 'submit',
            value: 'LAUNCH',
            disabled: false
          }
        }
      ],
      submit (data) {
        const dateTime = Vue.moment(data[2].replace(/\.\d{3}.+$/gi, ''));
        const deadline = dateTime.unix(); // Get timestamp next date
        const res = {
          subject: data[0],
          description: data[1],
          deadlineTime: deadline
        };
        store.commit('newVotingPending', true);
        store.commit('setMailPending', 'newVoting');
        store.dispatch('createVotingDapp', res);
      },
      reset () {
        console.log('reset');
      }
    }
  };
}

export function requestVote (data): any {
  const SELECT = [
    { value: null, text: 'Please select ...', disabled: true },
    { value: true, text: 'Approve' },
    { value: false, text: 'Reject' }
  ];
  return {
    title: 'VOTE FOR PROPOSAL',
    descr1: 'Please provide the information below in order to vote for the current proposal. ',
    descr2:
      'Once you have click on ‘VOTE’ confirm the transaction via Metamask. Only at that point your upload will be available.',
    form: {
      elements: [
        {
          type: 'input',
          data: {
            type: 'text',
            name: 'title-input',
            label: 'Title',
            placeholder: data.subject,
            disabled: true
          }
        },
        {
          type: 'input',
          data: {
            type: 'textarea',
            name: 'desc-textarea',
            label: 'Description',
            placeholder: data.description,
            disabled: true
          }
        },
        {
          type: 'input',
          data: {
            type: 'select',
            label: 'Vote',
            name: 'receiver-select',
            model: SELECT[0].value,
            options: SELECT,
            disabled: false,
            required: true
          }
        },
        {
          type: 'button',
          data: {
            type: 'submit',
            value: 'VOTE',
            disabled: false
          }
        },
        {
          type: 'text',
          data: {
            value: '// your vote count for ' + store.getters.getVotePercent + '% of all votes'
          }
        }
      ],
      submit (formInput) {
        const res = {
          votingId: data.id,
          verdict: formInput[2]
        };
        store.commit('newVotePending', data.id);
        store.dispatch('voteDapp', res);
      },
      reset () {
        console.log('reset');
      }
    }
  };
}

export function requestNewOrder (ethToUsdCourse, isxDaiNetwork): any {
  const MODE_SELECT = [
    { value: null, text: 'Please select ...', disabled: true },
    { value: 1, text: 'Buy' },
    { value: 2, text: 'Sell' }
  ];
  return {
    title: 'PLACE YOUR ORDER',
    descr1: 'In order to place your order in the company marketplace we need few infos.',
    descr2: 'Once you have click on ‘PLACE ORDER’ to confirm the transaction via Metamask.',
    form: {
      elements: [
        {
          type: 'input',
          data: {
            type: 'select',
            name: 'mode-select',
            label: 'Buy or Sell?',
            model: MODE_SELECT[0].value,
            options: MODE_SELECT,
            required: true
          }
        },
        {
          type: 'input',
          data: {
            type: 'number',
            name: 'number-input',
            id: 'order-amount',
            label: 'Amount of shares',
            model: '',
            min: validData.token.amount.min,
            max: store.getters.getTokenBalance() - store.getters.getFreezedOf,
            pattern: validData.tradeNow.amountOfShares.pattern,
            required: true
          }
        },
        {
          type: 'input',
          data: {
            type: 'number',
            name: 'price-order',
            label: `Price per share ${ !isxDaiNetwork ? '(ETH)' : '(USD)' }`,
            model: '',
            min: validData.order.price.min,
            pattern: validData.ethMinValuePattern,
            required: true
          }
        },
        {
          type: 'text',
          data: {
            class: ` ${!isxDaiNetwork ? 'text-custom-pos' : 'text-custom-pos-none'} `,
            name: 'approximate',
            value: '// USD = $ 0',
            approximateValue: ethValue =>
              '// USD = $ ' +
              formatter.format(ethToUsdCourse ? parseFloat((ethValue * ethToUsdCourse).toFixed(2)) : 0)
          }
        },
        {
          type: 'button',
          data: {
            type: 'submit',
            value: 'PLACE ORDER',
            disabled: false
          }
        }
      ],
      submit (data) {
        store.commit('newOrderPending', true);
        const requestData = {
          mode: data[0],
          amount: parseInt(data[1]),
          price: `${data[2]}`
        };
        store.dispatch('addMarketOrderDapp', requestData);
      },
      reset () {
        console.log('reset');
      }
    }
  };
}

export function requestTradeNow (orderData): any {
  const getMax = orderData => {
    const amount = Number.parseInt(orderData.amount.replace(',', ''));
    if (orderData.buySell === 'Buy') {
      const holding = store.getters.getTokenBalance() - store.getters.getFreezedOf;
      return amount < holding ? amount : holding;
    } else if (orderData.buySell === 'Sell') {
      return amount;
    }
  };
  return {
    title: 'TRADE NOW',
    descr1: 'In order to place your order in the company marketplace we need few infos.',
    descr2:
      'Once you have click on ‘TRADE NOW’ confirm the transaction via Metamask. Only at that point your order will be submitted.',
    form: {
      elements: [
        {
          type: 'input',
          data: {
            type: 'text',
            label: ' ',
            name: 'buy-input',
            model: orderData.buySell === 'Sell' ? 'Buy' : 'Sell ',
            disabled: true,
            required: true
          }
        },
        {
          type: 'input',
          data: {
            type: 'number',
            name: 'amount-input',
            label: `Amount of shares`,
            min: validData.companyUpdate.min,
            max: getMax(orderData),
            pattern: validData.tradeNow.amountOfShares.pattern,
            model: '',
            required: true
          }
        },
        {
          type: 'text',
          data: {
            class: 'text-custom-pos',
            name: 'insufficient',
            value: '',
            getMessage: () =>
              '// insufficient funds for the operation. your balance ' + store.getters.getBalance
          }
        },
        {
          type: 'input',
          data: {
            type: 'number',
            label: 'Price per share',
            name: 'price-input',
            model: orderData.orderPrice,
            disabled: true,
            required: true
          }
        },
        {
          type: 'button',
          data: {
            type: 'submit',
            value: 'TRADE NOW',
            disabled: false
          }
        }
      ],
      submit (data) {
        const tradeData = {
          orderId: parseInt(orderData.orderId),
          amount: parseInt(data[1]),
          fee: 0,
        };
        if (orderData.buySell === 'Sell') {
          store.commit('setMailPending', 'tradeNow');
          store.dispatch('countTokenPaymentByOrderIdDapp', tradeData);
        } else {
          store.commit('setMailPending', 'tradeNow');
          store.dispatch('addMarketTradeDapp', tradeData);
        }
      },
      reset () {
        console.log('reset');
      }
    }
  };
}

export function requestConfirmTrade (tradeProps): any {
  return {
    title: 'OFFER TO BUY',
    descr1: 'You have an offer for your order placed in the Marketplace',
    descr2:
      'Once you have click on ‘ACCEPT’ confirm the transaction via Metamask. Only at that point your order will be submitted.',
    form: {
      elements: [
        {
          type: 'input',
          data: {
            type: 'number',
            name: 'amount-input',
            label: 'Amount of shares',
            model: +tradeProps.fullAmount.replace(',', '') - +tradeProps.amount.replace(',', ''),
            required: true,
            disabled: true
          }
        },
        {
          type: 'input',
          data: {
            type: 'number',
            label: 'Price per share',
            name: 'price-input',
            model: tradeProps.orderPrice,
            disabled: true,
            required: true
          }
        },
        {
          type: 'button',
          data: {
            type: 'submit',
            value: 'ACCEPT',
            disabled: false
          }
        }
      ],
      submit () {
        store.dispatch('countOrderPaymentsDapp', tradeProps);
      },
      reset () {
        console.log('reset');
      }
    }
  };
}

export function requestUploadFinStatFile (): any {
  return {
    title: 'UPLOAD A NEW DOCUMENT',
    descr1: 'Please select the file you would like to upload by using the function below.',
    descr2:
      'Once you have click on ‘UPLOAD’ confirm the transaction via Metamask. Only at that point your upload will be available.',
    form: {
      elements: [
        {
          type: 'input',
          data: {
            type: 'text',
            name: 'title-input',
            label: 'Title',
            model: '',
            min: validData.finStatFile.title.min,
            max: validData.finStatFile.title.max,
            required: true
          }
        },
        {
          type: 'input',
          data: {
            type: 'file',
            name: 'file-input',
            label: 'Select file',
            model: null,
            accept: '.doc, .docx, .xls, .xlsx, .pdf, .txt',
            placeholder: 'Browse your files…',
            required: false
          }
        },
        {
          type: 'button',
          data: {
            type: 'submit',
            value: 'UPLOAD',
            disabled: false
          }
        }
      ],
      submit (data) {
        store.commit('finStatementPending', true);
        const def = '';
        const formData = {
          title: data[0],
          name: data[1].name,
          file: data[1]
        };

        if (formData.file) {
          store.getters.ipfs.add({ file: formData.file }).then(
            response => {
              addFinState(response);
            },
            error => {
              console.log('error');
              console.log(error);
            }
          );
        } else {
          addFinState(def); // '' - none
        }

        function addFinState (hashfile) {
          const update = {
            title: formData.title,
            name: formData.name,
            file: hashfile
          };
          if (update.title !== def || update.file !== def) {
            store.commit('setMailPending', 'uploadFile');
            store.dispatch('addFinStatementDapp', update)
          } else {
            console.log('error');
          }
        }
      },
      reset () {
        console.log('reset');
      }
    }
  };
}

export function requestUploadCompanyAccount (type, updateTransactionsCallback): any {
  return {
    title: 'UPLOAD COMPANY ACCOUNT',
    descr1: 'Please paste the address you would like to record as official company wallet.',
    descr2:
      'Once you have click on ‘UPDATE’ confirm the transaction via Metamask. Only at that point your upload will be available.',
    form: {
      elements: [
        {
          type: 'input',
          data: {
            type: 'text',
            name: 'currency-input',
            label: 'Currency',
            model: type === 'BTC' ? 'Bitcoin (BTC)' : type === 'ETH' ? 'Ethereum (ETH)' : '',
            min: validData.financial.currency.min,
            max: validData.financial.currency.max,
            required: false,
            disabled: true
          }
        },
        {
          type: 'input',
          data: {
            type: 'text',
            name: 'address-input',
            label: 'Address',
            model: '',
            min: validData.financial.currency.min,
            max: validData.financial.currency.max,
            address: validData.financial[type],
            required: true
          }
        },
        {
          type: 'button',
          data: {
            type: 'submit',
            value: 'UPDATE',
            disabled: false
          }
        }
      ],
      submit (data) {
        switch (type) {
          case 'BTC':
            store.commit('companyWalletBtcPending', true);
            store.dispatch('setCompanyBitcoinWalletDapp', { address: data[1].trim() });
            updateTransactionsCallback(data[1].trim());
            break;
          case 'ETH':
            store.commit('companyWalletEthPending', true);
            store.dispatch('setCompanyEthereumWalletDapp', { address: data[1].trim() });
            updateTransactionsCallback(null, data[1].trim());
            break;
          default:
            return;
        }
      },
      reset () {
        console.log('reset');
      }
    }
  };
}

export function requestUploadBankCompanyAccount (account, updateTransactionsCallback): any {
  return {
    title: 'UPLOAD COMPANY ACCOUNT',
    descr1: 'Please paste the address you would like to record as official company wallet.',
    descr2:
      'Once you have click on ‘UPDATE’ confirm the transaction via Metamask. Only at that point your upload will be available.',
    form: {
      elements: [
        {
          type: 'input',
          data: {
            type: 'text',
            name: 'bank-name-input',
            label: 'Bank Name',
            model: account.name ? account.name : '',
            min: validData.financial.currency.min,
            max: validData.financial.currency.max,
            required: true
          }
        },
        {
          type: 'input',
          data: {
            type: 'text',
            name: 'currency-input',
            label: 'Currency',
            model: 'USD',
            placeholder: 'USD',
            min: validData.financial.currency.min,
            max: validData.financial.currency.max,
            required: true,
            disabled: true
          }
        },
        {
          type: 'input',
          data: {
            type: 'text',
            name: 'value-input',
            label: 'Amount',
            model: account.value ? account.value : '',
            required: true,
            pattern: /^[^0](\d*[\,\.]?\d{1,2})$|0([\,\.]\d{1,2})$/
          }
        },
        {
          type: 'button',
          data: {
            type: 'submit',
            value: 'UPDATE',
            disabled: false
          }
        }
      ],
      submit (data) {
        store.commit('companyWalletBankPending', true);
        store.dispatch('setCompanyBankWalletDapp', {
          name: data[0],
          currency: data[1],
          value: data[2]
        });
      },
      reset () {
        console.log('reset');
      }
    }
  };
}

export function requestUploadLegalFile (): any {
  return {
    title: 'UPLOAD A NEW DOCUMENT',
    descr1: 'Please select the file you would like to upload by using the function below.',
    descr2:
      'Once you have click on ‘UPLOAD’ confirm the transaction via Metamask. Only at that point your upload will be available.',
    form: {
      elements: [
        {
          type: 'input',
          data: {
            type: 'text',
            name: 'title-input',
            label: 'Title',
            model: '',
            min: validData.legalFile.title.min,
            max: validData.legalFile.title.max,
            required: true
          }
        },
        {
          type: 'input',
          data: {
            type: 'file',
            name: 'file-input',
            label: 'Select file',
            model: null,
            accept: '.txt, .doc, .pdf, .xls, .docx',
            placeholder: 'Browse your files…',
            required: true
          }
        },
        {
          type: 'button',
          data: {
            type: 'submit',
            value: 'UPLOAD',
            disabled: false
          }
        }
      ],
      submit (data) {
        store.commit('legalDocumentPending', true);
        const def = '';
        let name = '';

        if (data[1]) {
          name = data[1].name;
          store.getters.ipfs.add({ file: data[1] }).then(
            response => {
              addLegalDocument(response);
            },
            error => {
              console.log('error');
            }
          );
        } else {
          addLegalDocument(def); // '' - none
        }

        function addLegalDocument (hashfile) {
          const update = {
            title: data[0],
            name,
            file: hashfile
          };
          if (update.title !== def || update.file !== def || update.name !== def) {
            store.commit('setMailPending', 'uploadDocument');
            store.dispatch('addLegalDocumentDapp', update)
          } else {
            console.log('error');
          }
        }
      },
      reset () {
        console.log('reset');
      }
    }
  };
}

export function requestFeature (): any {
  return {
    title: 'APPROVE NEW FEATURE',
    descr1: 'Good news! A new features is now available to your company.',
    descr2:
      'Once you have click on ‘APPROVE’ confirm the transaction via Metamask. Only at that point your upload will be available.',
    form: {
      elements: [
        {
          type: 'input',
          data: {
            type: 'textarea',
            name: 'textarea-input',
            label: 'Description',
            model: '',
            required: true
          }
        },
        {
          type: 'input',
          data: {
            type: 'file',
            name: 'file-input',
            label: 'Select file',
            model: null,
            accept: '.jpg, .png, .gif',
            placeholder: 'Browse your files…',
            required: false
          }
        },
        {
          type: 'button',
          data: {
            type: 'submit',
            value: 'UPLOAD',
            disabled: false
          }
        }
      ],
      submit (data) {
        console.log('submit:', data);
      },
      reset () {
        console.log('reset');
      }
    }
  };
}

export function requestReviewTransfer (props): any {
  return {
    title: 'REVIEW TRANSFER',
    descr1:
      "Here's a new transfer for your review. Continue by clicking 'Approve' to confirm this transfer. By clicking 'Reject', this transfer will not occur. ",
    descr2:
      'Once you have click on ‘APPROVE’ confirm the transaction via Metamask. Only at that point your upload will be available.',
    form: {
      elements: [
        {
          type: 'input',
          data: {
            type: 'text',
            label: 'Receiver',
            name: 'Full name',
            model: props.receiver,
            disabled: true,
            required: false
          }
        },
        {
          type: 'input',
          data: {
            type: 'text',
            label: 'Sender',
            name: 'Full name',
            model: props.sender,
            disabled: true,
            required: false
          }
        },
        {
          type: 'input',
          data: {
            type: 'text',
            label: 'Amount of shares',
            name: 'price-input',
            model: props.amount,
            disabled: true,
            required: true
          }
        },
        {
          type: 'button',
          data: {
            type: 'reset',
            value: 'REJECT',
            disabled: false
          }
        },
        {
          type: 'button',
          data: {
            type: 'submit',
            value: 'APPROVE',
            disabled: false
          }
        }
      ],
      submit () {
        store.commit('setMailPending', 'reviewEscrow');
        store.dispatch('approveTokenTransferDapp', { id: props.id, desc: '', props })
      },
      reset () {
        store.commit('setMailPending', 'reviewEscrow');
        store.dispatch('rejectTokenTransferDapp', { id: props.id, desc: '' });
      }
    }
  };
}

export function requestReviewTrade (props): any {
  return {
    title: 'REVIEW TRADE',
    descr1:
      "Here's a new trade for your review. Continue by clicking 'Approve' to confirm this trade. By clicking 'Reject', this trade will not occur.",
    descr2:
      "Please note that this decision can only be made once, after clicking 'Approve' or 'Reject' this trade review will be completed.",
    form: {
      elements: [
        {
          type: 'input',
          data: {
            type: 'text',
            label: 'Reciever',
            name: 'Full name',
            model: props.receiver,
            disabled: true,
            required: false
          }
        },
        {
          type: 'input',
          data: {
            type: 'text',
            label: 'Sender',
            name: 'Full name',
            model: props.sender,
            disabled: true,
            required: false
          }
        },
        {
          type: 'input',
          data: {
            type: 'text',
            label: 'Amount of shares',
            name: 'price-input',
            model: props.amount,
            disabled: true,
            required: true
          }
        },
        {
          type: 'input',
          data: {
            type: 'text',
            label: 'Price per share',
            name: 'price-input',
            model: props.price,
            disabled: true,
            required: true
          }
        },
        {
          type: 'button',
          data: {
            type: 'reset',
            value: 'REJECT',
            disabled: false
          }
        },
        {
          type: 'button',
          data: {
            type: 'submit',
            value: 'APPROVE',
            disabled: false
          }
        }
      ],
      submit () {
        store.commit('setMailPending', 'reviewEscrow');
        store.dispatch('approveTradeTransferDapp', { id: props.id, desc: '', props })
      },
      reset () {
        store.commit('setMailPending', 'reviewEscrow');
        store.dispatch('rejectTradeTransferDapp', { id: props.id, desc: '' });
      }
    }
  };
}

export function requestGettingContact (replyData, isReply): any {
  console.log(replyData, 'replyData');
  return {
    title: 'GET IN CONTACT',
    descr1:
      'Please provide the information below in order to send a new message and click on ‘SEND’.',
    descr2: '',
    form: {
      elements: [
        {
          type: 'input',
          data: {
            type: 'select',
            name: 'email-select',
            label: ' ',
            model:
              replyData && replyData.person_id === 2
                ? EMAIL_SELECT[1].value
                : isReply ? EMAIL_SELECT[1].value : EMAIL_SELECT[0].value ,
            options: EMAIL_SELECT,
            required: true,
            disabled: !!replyData
          }
        },
        {
          type: 'input',
          data: {
            type: 'text',
            name: 'subject-input',
            label: 'Subject',
            model: replyData ? replyData.subject : '',
            placeholder: '',
            required: true
          }
        },
        {
          type: 'input',
          data: {
            type: 'textarea',
            name: 'message-input',
            label: 'Description',
            model: '',
            placeholder: '',
            required: true
          }
        },
        {
          type: 'button',
          data: {
            type: 'submit',
            value: 'SEND',
            disabled: false
          }
        }
      ],
      submit (data) {
        store.getters.mails.notify({
          to: data[0],
          subject: data[1],
          text: data[2],
          companyName: store.getters.getCompanyName
        });
      },
      reset () {
        console.log('reset');
      }
    }
  };
}

export function requestCommunication (callback): any {
  return {
    title: 'COMMUNICATION',
    descr1:
      'Please provide the information below in order to send a new message and click on ‘PUBLISH’.',
    descr2: '',
    form: {
      elements: [
        {
          type: 'input',
          data: {
            type: 'textarea',
            name: 'message-input',
            label: 'Message',
            model: '',
            min: validData.communication.message.min,
            placeholder: '',
            required: true
          }
        },
        {
          type: 'button',
          data: {
            type: 'submit',
            value: 'PUBLISH',
            disabled: false
          }
        }
      ],
      submit (data) {
        store.getters.communications
          .add({
            messageText: data[0]
          })
          .then(result => callback(result, null))
          .catch(err => callback(null, err));
      },
      reset () {
        console.log('reset');
      }
    }
  };
}

export function requestStripe(): any {
  return {
    title: 'PAYMENT',
    descr1:
      'Please provide the information below in order to send a new message and click on ‘PUBLISH’.',
    descr2: '',
    form: {
      preventClose: true,
      elements: [
        {
          type: 'input',
          data: {
            type: 'email',
            label: 'Email',
            name: 'Email',
            model: '',
            placeholder: '',
            required: true
          }
        },
        {
          type: 'input',
          data: {
            type: 'text',
            label: 'Card Holder Name',
            name: 'payer-name',
            model: '',
            placeholder: '',
            required: true
          }
        },
        {
          type: 'input',
          data: {
            type: 'number',
            label: 'Amount (USD)',
            name: 'amount-input',
            model: 0,
            disabled: false,
            required: true
          }
        },
        {
          type: 'payment',
          data: {
            type: 'stripe',
            name: 'stripe',
            label: 'Payment',
            model: '',
            placeholder: '',
            required: true
          }
        },
        {
          type: 'button',
          data: {
            type: 'submit',
            value: 'PAY',
            disabled: false
          }
        }
      ],
      submit (data) {
        console.log(data);
      }, // Defined in StripeComponent
      reset () {
        console.log('reset');
      }
    }
  };
}
